import { TRACKING_EVENTS } from './events';
import { trackEvent } from './service/trackEvent';
import type { SearchItemType, SourceScreen } from './types';

// Extract a consistent errorType string from various error shapes
export const getErrorType = (error: unknown): string => {
  if (!error) return 'unknown';
  if (typeof error === 'string') return error;
  const e = error as Record<string, unknown>;
  return String(e.errorMessage || e.message || e.code || e.status || 'unknown');
};

// Maps search LEVELS values to SearchItemType for tracking
const LEVEL_TO_SEARCH_ITEM_TYPE: Record<string, SearchItemType> = {
  Parts: 'part',
  Operations: 'job',
  ProductionBatches: 'batch',
  ProductionOrders: 'productionOrder',
  NCFiles: 'ncFile',
  Tools: 'tool',
  ImagesDocuments: 'document',
};

export const levelToSearchItemType = (level: string): SearchItemType => {
  return LEVEL_TO_SEARCH_ITEM_TYPE[level] || 'part';
};

// Maps API entity item types to SearchItemType for tracking
const ITEM_TYPE_TO_SEARCH_ITEM_TYPE: Record<string, SearchItemType> = {
  Part: 'part',
  ProductionOperation: 'job',
  ProductionBatch: 'batch',
  ProductionOrder: 'productionOrder',
  NcFile: 'ncFile',
  Tool: 'tool',
  ImageDocument: 'document',
  OtherDocument: 'document',
};

export const itemTypeToSearchItemType = (itemType: string): SearchItemType => {
  return ITEM_TYPE_TO_SEARCH_ITEM_TYPE[itemType] || 'part';
};

// Tracks search result tapped — call from each entity's open()/onRowClick handler
export const trackSearchResultTapped = (itemType: string): void => {
  trackEvent(TRACKING_EVENTS.SEARCH, {
    action: 'resultTapped',
    resultItemType: itemTypeToSearchItemType(itemType),
  });
};

// Infer sourceScreen from the current React Navigation route name
// we need this to correctly define where user came to Details screen FROM,
// otherwise default to search screen
const ROUTE_TO_SOURCE_SCREEN: [string, SourceScreen][] = [
  ['wallboard', 'wallboard'],
  ['operatorpanel', 'operatorPanel'],
  ['planboard', 'planboard'],
  ['search', 'search'],
  ['now', 'live'],
  ['live', 'live'],
  ['performance', 'performance'],
  ['utilization', 'performance'],
  ['history', 'history'],
  ['batches', 'history'],
  ['timeline', 'timeline'],
  ['scheduled', 'scheduled'],
  ['batchdetails', 'batchDetails'],
  ['ncfiledetails', 'ncFileDetails'],
  ['jobdetails', 'jobDetails'],
  ['partdetails', 'partDetails'],
  ['toolsdetails', 'toolDetails'],
  ['productionorderdetails', 'productionOrderDetails'],
];

export const inferSourceScreen = (routeName?: string): SourceScreen => {
  if (!routeName) return 'search';
  const lower = routeName.toLowerCase();
  return ROUTE_TO_SOURCE_SCREEN.find(([key]) => lower.includes(key))?.[1] || 'search';
};

// map perspective screen keys to human-readable display names for Info Bar event tracking
// instead of "fromPerspective": "BatchDetailsNCFile", "toPerspective": "BatchDetailsPart" ->
//"fromPerspective": "Batch > NC File", "toPerspective": "Batch > Part"
const PERSPECTIVE_DISPLAY_NAMES: Record<string, string> = {
  // Batch context
  BatchDetailsPart: 'Batch > Part',
  BatchDetailsJobs: 'Batch > Job',
  BatchDetailsBatch: 'Batch > Batch',
  BatchDetailsNCFile: 'Batch > NC File',
  BatchDetailsTools: 'Batch > Tool',
  // NC File context
  NCFileDetailsPart: 'NC File > Part',
  NCFileDetailsJobs: 'NC File > Job',
  NCFileDetailsBatch: 'NC File > Batch',
  NCFileDetailsNCFile: 'NC File > NC File',
  NCFileDetailsTools: 'NC File > Tool',
  // Job context
  JobDetailsPart: 'Job > Part',
  JobDetailsJobs: 'Job > Job',
  JobDetailsBatch: 'Job > Batch',
  JobDetailsNCFile: 'Job > NC File',
  JobDetailsTools: 'Job > Tool',
  // Part context
  PartDetailsPart: 'Part > Part',
  PartDetailsJobs: 'Part > Job',
  PartDetailsBatch: 'Part > Batch',
  PartDetailsNCFile: 'Part > NC File',
  PartDetailsTools: 'Part > Tool',
  // Production Order context
  ProductionOrderDetailsPart: 'Production Order > Part',
  ProductionOrderDetailsBatch: 'Production Order > Batch',
  ProductionOrderDetailsNCFile: 'Production Order > NC File',
  // Tools context
  ToolsDetailsPart: 'Tool > Part',
  ToolsDetailsJobs: 'Tool > Job',
  ToolsDetailsBatch: 'Tool > Batch',
  ToolsDetailsNCFile: 'Tool > NC File',
  ToolsDetailsTools: 'Tool > Tool',
};

export const formatPerspectiveName = (perspectiveKey: string): string => {
  return PERSPECTIVE_DISPLAY_NAMES[perspectiveKey] || perspectiveKey;
};

// Maps detail screen name constants to TRACKING_EVENTS for item detail opened events
export const SCREEN_TO_TRACKING_EVENT: Record<string, string> = {
  BatchDetails: TRACKING_EVENTS.BATCH,
  NCFileDetailsV2: TRACKING_EVENTS.NC_FILE,
  JobDetailsV2: TRACKING_EVENTS.JOB,
  ProductionOrderDetailsV2: TRACKING_EVENTS.PRODUCTION_ORDER,
  PartDetailsV2: TRACKING_EVENTS.PART,
  ToolsDetailsV2: TRACKING_EVENTS.TOOL,
};
